import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import FilledButton from '../components/FilledButton';
import {
  validateResetToken,
  confirmPasswordReset,
} from '../services/api';

const initialCriteria = {
  length: false,
  uppercase: false,
  lowercase: false,
  number: false,
  specialChar: false,
};

export default function ResetPasswordConfirmPage() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('loading');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState(initialCriteria);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    validateResetToken(uid, token)
      .then((email) => {
        setEmail(email);
        setStatus('ready');
      })
      .catch(() => {
        setStatus('invalid');
      });
  }, [uid, token]);

  const updateCriteria = (value) => {
    setPasswordCriteria({
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    const allMet =
      passwordCriteria.length &&
      passwordCriteria.uppercase &&
      passwordCriteria.lowercase &&
      passwordCriteria.number &&
      passwordCriteria.specialChar;
    if (!allMet) return;

    setSubmitting(true);
    try {
      await confirmPasswordReset(uid, token, newPassword);
      navigate('/sign-in', {
        state: { successMessage: 'Password has been reset. You may now sign in.' },
      });
    } catch (err) {
      setErrorMessage('Reset link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  const criteriaMet =
    passwordCriteria.length &&
    passwordCriteria.uppercase &&
    passwordCriteria.lowercase &&
    passwordCriteria.number &&
    passwordCriteria.specialChar;

  return (
    <Box sx={{ textAlign: 'center', py: { xs: 6, md: 8 } }}>
      <Typography variant="h4" gutterBottom>
        Reset Password
      </Typography>
      {status === 'loading' && <Typography>Loading...</Typography>}
      {status === 'invalid' && (
        <Typography color="error">Reset link is invalid or has expired.</Typography>
      )}
      {status === 'ready' && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 320, mx: 'auto' }}
        >
          {email && (
            <Typography sx={{ mb: 1 }}>Resetting password for <strong>{email}</strong></Typography>
          )}
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              New Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="••••••"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                updateCriteria(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNew((v) => !v)}
                      sx={{ p: 0, width: '1.5rem', height: '1.5rem', border: 0, bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' }, '&:active': { bgcolor: 'transparent' } }}
                    >
                      {showNew ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography component="div" variant="caption" sx={{ mt: 0.5 }}>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li style={{ color: passwordCriteria.length ? 'green' : 'red' }}>
                  At least 8 characters
                </li>
                <li style={{ color: passwordCriteria.uppercase ? 'green' : 'red' }}>
                  At least one uppercase letter
                </li>
                <li style={{ color: passwordCriteria.lowercase ? 'green' : 'red' }}>
                  At least one lowercase letter
                </li>
                <li style={{ color: passwordCriteria.number ? 'green' : 'red' }}>
                  At least one number
                </li>
                <li style={{ color: passwordCriteria.specialChar ? 'green' : 'red' }}>
                  At least one special character
                </li>
              </ul>
            </Typography>
          </div>
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Confirm New Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="••••••"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword && confirmPassword !== newPassword}
              helperText={
                confirmPassword && confirmPassword !== newPassword
                  ? 'Passwords do not match.'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((v) => !v)}
                      sx={{ p: 0, width: '1.5rem', height: '1.5rem', border: 0, bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' }, '&:active': { bgcolor: 'transparent' } }}
                    >
                      {showConfirm ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <FilledButton
            type="submit"
            text="Set Password"
            size="small"
            color="primary"
            disabled={submitting || !criteriaMet || !confirmPassword || confirmPassword !== newPassword}
          />
        </Box>
      )}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
